import React from "react";
import { Box, Typography } from "@material-ui/core";
import { DateRangePicker } from "halifax";
import dayjs from "dayjs";

import "./styles.scss";
import {
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
} from "@capone/common";
import { CallState } from "redmond";
import { RouteComponentProps } from "react-router";
import { AVAILABLE, getExperimentVariant } from "@capone/experiments";
import clsx from "clsx";
import { parseQueryString } from "../../../../utils/parseQueryString";
import {
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../../../context/experiments";
import { MonthAndDatePickerConnectorProps } from "./container";

export interface IMonthAndDatePickerProps
  extends MonthAndDatePickerConnectorProps,
    RouteComponentProps {
  viewType: MonthAndDatePickerType;
  header?: JSX.Element | string;
  // focusedMonthIndex must be given in order to make columnView focus on an arbitrary month
  focusedMonthIndex?: number;
  // setFocusedMonthIndex must be given for working with the groupView + columnView combination
  setFocusedMonthIndex?: (index: number) => void;
  setFromDate?: (date: Date | null) => void;
  setUntilDate?: (date: Date | null) => void;
  from: Date | null;
  until: Date | null;
  className?: string;
  showPricing?: boolean;
}

export const MonthAndDatePicker = (props: IMonthAndDatePickerProps) => {
  const {
    pickerType,
    from,
    until,
    setFromDate,
    setUntilDate,
    header,
    viewType,
    className,
    priceTags,
    months,
    showPricing = true,
    priceBucketsCallState,
    searchLocation,
    history,
  } = props;

  const parsedQueryString = parseQueryString(history);
  const expState = useExperiments();

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  React.useEffect(() => {
    if (
      [CallState.Success, CallState.Failed].includes(priceBucketsCallState) ||
      (!searchLocation && !parsedQueryString?.locationName) // note: has to check for queryString too bc searchLocation is undefined at first since it's set in `MobileSearchControlV3` after initial render
    ) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.CALENDAR_LOADED,
        funnel: TravelAppInterfaceFunnelType.HOTELS,
      });
    }
  }, [priceBucketsCallState, parsedQueryString?.locationName]);

  return (
    <>
      {header && (
        <Box className="month-and-date-picker-header-container">
          {typeof header === "string" ? (
            <Typography variant="body2">{header}</Typography>
          ) : (
            header
          )}
        </Box>
      )}
      <DateRangePicker
        className={clsx(className, { xmod: isXmodRedesign })}
        minAllowedDate={dayjs().toDate()}
        maxAllowedDate={dayjs().add(1, "year").toDate()}
        minNumDays={1}
        startDate={from}
        endDate={until}
        setStartDate={setFromDate}
        setEndDate={setUntilDate}
        months={months}
        priceTags={priceTags}
        pickerType={pickerType}
        columnView={viewType === MonthAndDatePickerType.Column}
        showPricing={showPricing}
        showOverPricingText={false}
        priceRangeTagsHeader={
          showPricing ? "Prices per night starting around:" : undefined
        }
      />
    </>
  );
};

export enum MonthAndDatePickerType {
  Horizontal,
  Column,
  Group,
}
